import { MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";

const { color, bannerTone, density, radius, typeScale } = adminTokens;
const [, spaceSm] = adminTokens.spacing;
const spinnerSize = 16;
// The design's Buttons section pads the label 18px each side.
const labelPadding = 18;

export type ButtonLook = "primary" | "commit" | "secondary" | "destructive" | "destructiveOutline";
export type ButtonSize = "standard" | "dialog";

const lookStyles = {
  primary: css`
    background-color: ${color.periwinkle};
    border-color: ${color.periwinkle};
    color: ${color.navy};
    &:hover {
      background-color: ${color.periwinkleHover};
      border-color: ${color.periwinkleHover};
    }
  `,
  commit: css`
    background-color: ${color.navy};
    border-color: ${color.navy};
    color: ${color.surface};
  `,
  secondary: css`
    background-color: ${color.surface};
    border-color: ${color.navy};
    color: ${color.navy};
    &:hover {
      background-color: ${color.navy};
      color: ${color.surface};
    }
  `,
  destructive: css`
    background-color: ${color.errorStrong};
    border-color: ${color.errorStrong};
    color: ${color.surface};
  `,
  // The design's app error border and text, the same pair the error banner uses.
  destructiveOutline: css`
    background-color: ${color.surface};
    border-color: ${bannerTone.error.border};
    color: ${bannerTone.error.text};
  `,
};

const heights: Record<ButtonSize, number> = {
  standard: density.controlHeight,
  dialog: density.dialogControlHeight,
};

// On a phone every Button clears the 44px hit target, and a dialog's Buttons are
// 48px with a 15px label, as the design's full-screen steps set them
// (PHONE-R4, PHONE-R5).
const phoneHeights: Record<ButtonSize, number> = {
  standard: 44,
  dialog: 48,
};
const phoneDialogLabelSize = "15px";

const heightFor = (size: ButtonSize, isPhone: boolean) =>
  isPhone ? phoneHeights[size] : heights[size];

interface StyledButtonProps {
  $look: ButtonLook;
  $size: ButtonSize;
  $isIconOnly: boolean;
  $isPhone: boolean;
}

const unavailableStyle = css`
  background-color: ${color.disabledBackground};
  border-color: ${color.disabledBackground};
  color: ${color.disabledText};
  cursor: not-allowed;
`;

// The unavailable look (disabled, or pending via aria-disabled) is declared
// after the looks and repeats itself on hover, so no look's hover rule wins.
const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spaceSm}px;
  box-sizing: border-box;
  height: ${({ $size, $isPhone }) => heightFor($size, $isPhone)}px;
  min-width: ${({ $isIconOnly, $size, $isPhone }) =>
    $isIconOnly ? `${heightFor($size, $isPhone)}px` : "0"};
  padding: 0 ${({ $isIconOnly }) => ($isIconOnly ? 0 : labelPadding)}px;
  border: 1px solid transparent;
  border-radius: ${radius.pill}px;
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${({ $size, $isPhone }) =>
    $isPhone && $size === "dialog" ? phoneDialogLabelSize : typeScale.labelValue.fontSize};
  line-height: ${typeScale.labelValue.lineHeight};
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 150ms,
    color 150ms;

  ${({ $look }) => lookStyles[$look]}

  &:focus-visible {
    ${focusOutline}
  }

  &:disabled,
  &[aria-disabled="true"] {
    ${unavailableStyle}
    &:hover {
      ${unavailableStyle}
    }
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.svg`
  width: ${spinnerSize}px;
  height: ${spinnerSize}px;
  animation: ${spin} 0.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
`;

type PendingProps =
  | { isPending?: false; pendingLabel?: string }
  | { isPending: true; pendingLabel: string };

interface ButtonBaseProps {
  look: ButtonLook;
  size?: ButtonSize;
  disabled?: boolean;
  type?: "button" | "submit";
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export type ButtonProps = ButtonBaseProps & PendingProps & { children: ReactNode };

// The one control that starts an action on every admin screen (BUTTON-R1…R9).
// While pending it stays focusable but swallows activation, and cancels the
// native submit of a submit Button, so a second click, Enter or the form's
// implicit submission cannot fire a duplicate request (XCUT-R1).
export const Button = ({
  look,
  size = "standard",
  disabled = false,
  type = "button",
  isPending = false,
  pendingLabel,
  onClick,
  children,
}: ButtonProps) => {
  const isPhone = useIsPhoneLayout();
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (isPending) {
      event.preventDefault();
      return;
    }

    onClick?.(event);
  };

  return (
    <StyledButton
      type={type}
      $look={look}
      $size={size}
      $isIconOnly={false}
      $isPhone={isPhone}
      disabled={disabled}
      aria-disabled={isPending ? true : undefined}
      aria-busy={isPending ? true : undefined}
      onClick={handleClick}
    >
      {isPending && (
        <Spinner aria-hidden="true" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
          <path
            d="M21 12a9 9 0 0 0-9-9"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </Spinner>
      )}
      {isPending ? pendingLabel : children}
    </StyledButton>
  );
};

interface IconButtonProps extends ButtonBaseProps {
  accessibleName: string;
  icon: ReactNode;
}

// Icon-only: the accessible name is required by the type, so a nameless icon
// button cannot be written (BUTTON-R7).
export const IconButton = ({
  look,
  size = "standard",
  disabled = false,
  type = "button",
  onClick,
  accessibleName,
  icon,
}: IconButtonProps) => {
  const isPhone = useIsPhoneLayout();

  return (
    <StyledButton
      type={type}
      $look={look}
      $size={size}
      $isIconOnly
      $isPhone={isPhone}
      disabled={disabled}
      aria-label={accessibleName}
      onClick={onClick}
    >
      <IconSlot aria-hidden="true">{icon}</IconSlot>
    </StyledButton>
  );
};

interface ButtonLinkProps {
  look: ButtonLook;
  size?: ButtonSize;
  to: string;
  // A name fuller than the visible text, such as "Redo b72e0c4a" for "Redo" (ACCT-R7).
  accessibleName?: string;
  children: ReactNode;
}

// A Button that opens another admin screen: a link with a Button's look, so it opens in place,
// can be opened in a new tab and is read as a link (BUTTON-R12).
export const ButtonLink = ({
  look,
  size = "standard",
  to,
  accessibleName,
  children,
}: ButtonLinkProps) => {
  const isPhone = useIsPhoneLayout();

  return (
    <StyledButton
      as={Link}
      to={to}
      aria-label={accessibleName}
      $look={look}
      $size={size}
      $isIconOnly={false}
      $isPhone={isPhone}
    >
      {children}
    </StyledButton>
  );
};
